/**
 * Helpers and constants for the Barley Plant Density / Seed Rate feature.
 *
 * Spec: barley-feature-spec.md
 * Proxy:        POST /proxy-layer/v1/barley-density
 */

export type Coordinates = [number, number];

export const BARLEY_DENSITY_SUPPORTED_COUNTRIES = [
  'Czech Republic',
  'England',
  'France',
  'Germany',
  'Great Britain',
  'Hungary',
  'Poland',
  'Slovakia',
  'United Kingdom',
] as const;

export type BarleyDensitySupportedCountry = typeof BARLEY_DENSITY_SUPPORTED_COUNTRIES[number];

export const BARLEY_GRASS_WEED_COUNTRIES: BarleyDensitySupportedCountry[] = [
  'England',
  'Germany',
  'Great Britain',
  'United Kingdom',
];

export const BARLEY_PLANTING_CONDITIONS = [
  'Bad conditions',
  'Normal conditions',
  'Good conditions',
] as const;

export type BarleyPlantingCondition = typeof BARLEY_PLANTING_CONDITIONS[number];

export const DEFAULT_BARLEY_PLANTING_CONDITION: BarleyPlantingCondition = 'Normal conditions';

export const BARLEY_DENSITY_MODEL = { name: 'barley-density', version: '1.0.0' };

/** Czech Republic, Poland and Slovakia share the same barley yield bands. */
const CENTRAL_EUROPE_YIELD_RANGES: Record<string, string> = {
  '4-6': 'Low Yield (4-6tn/ha)',
  '6-8': 'Medium Yield (6-8tn/ha)',
  '8-12': 'High Yield (8-12tn/ha)',
};

/**
 * Yield level fallback map per country, used when the countries config
 * `YieldRange` entry has no `seedRateYieldRange` field (e.g. Germany barley).
 */
export const YIELD_LEVEL_MAP: Record<string, Record<string, string>> = {
  Germany: {
    '<8': 'Low to Medium (<8tn/ha)',
    '8-10': 'Medium to High (>8tn/ha)',
  },
  'Czech Republic': CENTRAL_EUROPE_YIELD_RANGES,
  Poland: CENTRAL_EUROPE_YIELD_RANGES,
  Slovakia: CENTRAL_EUROPE_YIELD_RANGES,
  Hungary: {
    '<5': 'Low Yield (<5tn/ha)',
    '5-7': 'Medium Yield (5-7tn/ha)',
    '>7': 'High Yield (>7tn/ha)',
  },
  France: {
    '<5': 'Low (<5tn/ha)',
    '5-7': 'Medium (5-7tn/ha)',
    '>7': 'High (>7tn/ha)',
    '<7': 'Low (<7tn/ha)',
    '7-9': 'Medium (7-9tn/ha)',
    '>9': 'High (>9tn/ha)',
  },
};

const UK_YIELD_RANGES: Record<string, string> = {
  '<9.5': 'Low (<9.5tn/ha)',
  '>9.5': 'High (>9.5tn/ha)',
};

YIELD_LEVEL_MAP['Great Britain'] = UK_YIELD_RANGES;
YIELD_LEVEL_MAP['United Kingdom'] = UK_YIELD_RANGES;
YIELD_LEVEL_MAP.England = UK_YIELD_RANGES;

export const isBarleyCrop = (cropName?: string | null): boolean =>
  (cropName ?? '').toString().toLowerCase() === 'barley';

export const isBarleyDensitySupportedCountry = (
  _isDDG: boolean,
  country?: string | null
): country is BarleyDensitySupportedCountry => {
  if (!country) {
    return false;
  }

  const trimmed = country.trim();

  return (BARLEY_DENSITY_SUPPORTED_COUNTRIES as readonly string[]).includes(trimmed);
};
export const supportsGrassWeedPressure = (country?: string | null): boolean =>
  !!country &&
  BARLEY_GRASS_WEED_COUNTRIES.includes(country.trim() as BarleyDensitySupportedCountry);

/**
 * Transforms an upstream product code into the API variety enum.
 *
 * SY_GALILEOO  -> "SY Galileoo"
 * SY_BARACOODA -> "SY Baracooda"
 *
 * Rules: "SY" stays uppercase, every other token is title-cased
 * (first char upper, rest lower).
 */
export const formatBarleyVarietyName = (productName: string): string => {
  if (!productName) {
    return '';
  }
  const tokens = productName.split('_').filter(Boolean);
  if (tokens.length === 0) {
    return '';
  }

  return tokens
    .map((token, idx) => {
      if (idx === 0 && token.toUpperCase() === 'SY') {
        return 'SY';
      }
      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    })
    .join(' ');
};

interface YieldRangeEntry {
  id?: string;
  label?: string;
  seedRateYieldRange?: string;
}

interface CropEntry {
  id?: number | string;
  name?: string;
  CropName?: string;
  YieldRange?: YieldRangeEntry[];
}

interface CountryConfigEntry {
  label?: string;
  name?: string;
  crops?: CropEntry[];
}

const normalizeYieldRangeId = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.join('-');
  }
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
};

/**
 * Resolves the yield_level enum required by the Barley Plant Density API.
 *
 *  1. Find the barley crop entry in the country config.
 *  2. Look up the YieldRange entry whose `id` (or `label`) matches the
 *     selected yieldRange from agronomicInputs.
 *  3. Prefer that entry's `seedRateYieldRange` value.
 *  4. Otherwise fall back to {@link YIELD_LEVEL_MAP} for the country.
 */
export const getBarleyYieldLevel = (
  country: string,
  selectedYieldRange: unknown,
  countriesConfig: CountryConfigEntry[] | undefined | null
): string | undefined => {
  const yieldId = normalizeYieldRangeId(selectedYieldRange);

  const countryEntry = (countriesConfig || []).find(
    (c) => (c?.label || c?.name || '').trim() === country.trim()
  );
  const barleyCrop = countryEntry?.crops?.find(
    (c) => (c?.CropName || c?.name || '').toString().toLowerCase() === 'barley'
  );

  const match = barleyCrop?.YieldRange?.find((yr) => yr?.id === yieldId || yr?.label === yieldId);

  if (match?.seedRateYieldRange) {
    return match.seedRateYieldRange;
  }

  return YIELD_LEVEL_MAP[country.trim()]?.[yieldId];
};

interface PolygonGeometry {
  type?: string;
  coordinates?: number[][][] | number[][];
}

interface FieldLike {
  geometry?: PolygonGeometry;
  reference_point?: { coordinates?: number[] };
}

const averagePoints = (points: number[][]): Coordinates | null => {
  if (!points.length) {
    return null;
  }
  let lng = 0;
  let lat = 0;
  for (const [x, y] of points) {
    lng += x;
    lat += y;
  }
  return [lng / points.length, lat / points.length];
};

const polygonRingPoints = (geometry?: PolygonGeometry): number[][] => {
  if (!geometry?.coordinates) {
    return [];
  }
  // Polygon: coordinates = [[ [lng,lat], ... ]]
  // Already-flat ring (defensive): coordinates = [ [lng,lat], ... ]
  const first = (geometry.coordinates as unknown[])[0];
  let ring: number[][];
  if (Array.isArray(first) && Array.isArray(first[0])) {
    ring = first as number[][];
  } else {
    ring = geometry.coordinates as number[][];
  }
  if (ring.length > 1) {
    const a = ring[0];
    const b = ring[ring.length - 1];
    if (a && b && a[0] === b[0] && a[1] === b[1]) {
      ring = ring.slice(0, -1);
    }
  }
  return ring.filter((p) => Array.isArray(p) && p.length >= 2);
};

/**
 * Computes a single [lng, lat] centroid representing all fields across all
 * properties on the recommendation. Uses `reference_point.coordinates` when
 * available, otherwise averages polygon vertices per field then averages the
 * per-field centroids.
 */
export const calculateFieldsCentroid = (
  fields: FieldLike[] | undefined | null
): Coordinates | null => {
  if (!fields || fields.length === 0) {
    return null;
  }

  const fieldCentroids: Coordinates[] = [];
  for (const field of fields) {
    const ref = field?.reference_point?.coordinates;
    if (Array.isArray(ref) && ref.length >= 2) {
      fieldCentroids.push([Number(ref[0]), Number(ref[1])]);
      continue;
    }
    const ringPoints = polygonRingPoints(field?.geometry);
    const centroid = averagePoints(ringPoints);
    if (centroid) {
      fieldCentroids.push(centroid);
    }
  }

  return averagePoints(fieldCentroids);
};
